<template>
  <div>
    <el-button type="primary" size="mini" @click="addRule">
      <i class="el-icon-plus"></i>
      Add Rule
    </el-button>
    <div
      v-for="(rule, ruleIndex) in formBuilder.conditional_approval_data_actions"
      :key="ruleIndex"
      class="rule-box"
    >
      <div class="rule-header">
        <h3 class="rule-title">Conditional Trigger [Ruleset#{{ ruleIndex + 1 }}]</h3>
        <el-link
          :underline="false"
          type="primary"
          size="mini"
          @click="removeRule(ruleIndex)"
          icon="el-icon-remove-outline"
        ></el-link>
      </div>

      <div class="section">
        <div class="section-header">
          <h4 class="section-title">Conditions</h4>
          <el-link
            :underline="false"
            icon="el-icon-circle-plus-outline"
            size="mini"
            @click="addCondition(rule)"
          >
            Add
          </el-link>
        </div>

        <div v-for="(condition, cIndex) in rule.conditions" :key="cIndex" class="row">
          <el-select
            v-model="condition.field"
            placeholder="Select Form Field"
            style="width: 180px"
            size="mini"
            clearable
            filterable
            @change="(changedValue) => checkCurrentFieldData(changedValue, condition)"
          >
            <el-option
              v-for="field in allFormbuilderFields"
              :key="field.template_key"
              :label="field.label"
              :value="field.template_key"
            ></el-option>
          </el-select>

          <el-select
            v-model="condition.operator"
            size="mini"
            :disabled="!condition.field"
            placeholder="Select Operator"
            clearable
            filterable
            style="width: 180px"
          >
            <el-option
              v-for="(operator, index) of getCurrentFieldOperators(condition)"
              :key="index"
              :label="operator.title"
              :value="operator.value"
            ></el-option>
            <el-option
              v-if="condition.input_type == 'SELECT' && condition.data_type == 'ARRAY'"
              value="=text-includes"
              label="Text Includes"
            ></el-option>
          </el-select>

          <el-select
            v-model="condition.value_type"
            placeholder="Select type"
            size="mini"
            style="width: 180px"
            clearable
            filterable
          >
            <el-option label="From Field" value="from_field"></el-option>
            <el-option label="Custom Value" value="custom_value"></el-option>
          </el-select>

          <el-select
            v-if="condition.value_type == 'from_field' && condition.data_type == 'OBJECT_ID'"
            v-model="condition.value"
            multiple
            collapse-tags
            size="mini"
            filterable
            style="width: 180px"
          >
            <el-option
              v-for="(temp, i) in entilistAsOptions(condition)"
              :key="temp._id + '_' + i"
              :value="temp._id"
              :label="getLabel(temp, i)"
            >
              {{ getLabel(temp, i) }}
            </el-option>
          </el-select>

          <el-select
            v-if="
              condition.value_type == 'from_field' &&
              ['SELECT', 'LIST', 'WEEKDAYS'].includes(condition.input_type)
            "
            v-model="condition.value"
            collapse-tags
            size="mini"
            :multiple="condition.operator == 'in' || condition.operator == 'nin' ? true : false"
            style="width: 180px"
          >
            <template v-if="fieldType(condition, 'LIST')">
              <el-option
                v-for="(enityData, index) of getCurrentFieldOptions(condition)"
                :key="index + '_' + enityData.value"
                :label="enityData.name"
                :value="enityData.value"
                multiple
                collapse-tags
              ></el-option>
            </template>
            <template v-else-if="fieldType(condition, 'WEEKDAYS')">
              <el-option
                v-for="(option, index) of weekdays_options"
                :value="option.key"
                :label="option.name"
                :key="index"
              ></el-option>
            </template>
            <template v-else>
              <el-option
                v-for="(enityData, index) of getCurrentFieldOptions(condition)"
                :key="index"
                :label="enityData"
                :value="enityData"
                multiple
                collapse-tags
              ></el-option>
            </template>
          </el-select>

          <el-input
            v-if="condition.value_type == 'custom_value' && condition.data_type !== 'OBJECT_ID'"
            v-model="condition.value"
            type="text"
            size="mini"
            placeholder="Enter value"
            :style="{ width: '180px' }"
          ></el-input>

          <el-select
            v-model="condition.query_type"
            placeholder="Select Query Type"
            style="width: 100px"
            size="mini"
          >
            <el-option label="AND" value="AND"></el-option>
            <el-option label="OR" value="OR"></el-option>
          </el-select>

          <!-- Remove condition -->
          <el-link
            :underline="false"
            type="primary"
            size="mini"
            @click="removeCondition(rule, cIndex)"
            icon="el-icon-remove-outline"
            style="width: 20px"
          ></el-link>
        </div>
      </div>

      <!-- Actions -->
      <div class="actions-section">
        <div class="actions-header">
          <h4 class="section-title">Actions</h4>
          <el-link
            :underline="false"
            icon="el-icon-circle-plus-outline"
            size="mini"
            @click="addAction(rule)"
          >
            Add
          </el-link>
        </div>

        <div v-for="(action, aIndex) in rule.actions" :key="aIndex" class="actions-row">
          <div class="actions-field">
            <span class="actions-label">On completion of workflow</span>
            <el-select
              v-model="action.workflow_level"
              placeholder="Workflow"
              size="mini"
              class="actions-input"
            >
              <el-option
                v-for="(lvl, index) in getLevels"
                :key="'Level-' + index"
                :label="'Level-' + (index + 1)"
                :value="'levelIndex-' + index"
              />
            </el-select>
          </div>

          <div class="actions-field">
            <span class="actions-label">Trigger</span>
            <el-select
              v-model="action.action_type"
              placeholder="Trigger"
              size="mini"
              class="actions-input"
            >
              <el-option label="Form" value="trigger_form_builder" />
              <el-option
                v-if="rule.actions.length > 1"
                label="Work Flow Condition Hold"
                value="put_workflow_condition_hold"
              />
              <el-option label="Update field of parent formbuilder data" value="update_field" />
            </el-select>
          </div>

          <div class="actions-field" v-if="action.action_type == 'trigger_form_builder'">
            <span class="actions-label">Trigger Form</span>
            <el-select
              placeholder="Trigger Form"
              v-model="action.trigger_formbuilder_id"
              clearable
              filterable
              size="mini"
              class="actions-input"
            >
              <el-option
                v-for="(op, i) in allFormbuilders.filter(
                  (e) => e.form_builders_owner !== 'ANONYMOUS' && e._id !== formBuilder._id
                )"
                :key="op._id + i"
                :value="op._id"
                :label="op.name"
              >
                {{ op.name }}
              </el-option>
            </el-select>
          </div>
          <div
            class="actions-field"
            v-if="action.action_type == 'trigger_form_builder' && action.trigger_formbuilder_id"
          >
            <span class="actions-label">Repeat Type</span>
            <el-select
              v-model="action.repeat_type"
              clearable
              filterable
              size="mini"
              class="actions-input"
            >
              <el-option label="One Time Per User" value="one_time_per_user" />
              <el-option label="Every Time" value="every_time" />
            </el-select>
          </div>
          <div
            class="actions-field"
            v-if="action.action_type == 'trigger_form_builder' && action.trigger_formbuilder_id"
          >
            <el-button
              type="text"
              icon="el-icon-setting"
              @click="openFormFieldsMapping(ruleIndex, aIndex, action)"
            >
              Map fields
            </el-button>
          </div>

          <div class="actions-field" v-if="action.action_type == 'put_workflow_condition_hold'">
            <span class="actions-label">Until workflow resume conditions</span>
            <el-select
              placeholder="Select Form"
              v-model="action.resume_formbuilder_id"
              clearable
              filterable
              size="mini"
              class="actions-input"
            >
              <el-option
                v-for="(op, i) in allFormbuilders.filter(
                  (e) => e.form_builders_owner !== 'ANONYMOUS' && e._id !== formBuilder._id
                )"
                :key="op._id + i"
                :value="op._id"
                :label="op.name"
              >
                {{ op.name }}
              </el-option>
            </el-select>
          </div>
          <div class="actions-field" v-if="action.action_type == 'update_field'">
            <span class="actions-label">Parent Form</span>
            <el-select
              placeholder="Select parent form"
              v-model="action.parent_formbuilder_details_id"
              clearable
              filterable
              size="mini"
              class="actions-input"
            >
              <el-option
                v-for="(op, i) in allFormbuilders.filter(
                  (e) => e.form_builders_owner !== 'ANONYMOUS' && e._id !== formBuilder._id
                )"
                :key="op._id + i"
                :value="op._id"
                :label="op.name"
              >
                {{ op.name }}
              </el-option>
            </el-select>
          </div>
          <div
            class="actions-field"
            v-if="action.action_type == 'update_field' && action.parent_formbuilder_details_id"
          >
            <el-button
              type="text"
              icon="el-icon-setting"
              @click="openParentFormFieldsMapping(ruleIndex, aIndex, action)"
            >
              Map fields
            </el-button>
          </div>

          <el-link
            :underline="false"
            type="primary"
            size="mini"
            @click="removeAction(rule, aIndex)"
            icon="el-icon-remove-outline"
            class="remove-action-btn"
          ></el-link>
        </div>
      </div>
    </div>
    <dialog-component
      :title="'Fields Mapping'"
      :visible="openFieldsMappingDialog"
      @before-close="closeFieldsMappingDialog"
      :isShowFooter="false"
      :containerWidth="'42%'"
      :containerHeight="'70%'"
    >
      <div class="p-3">
        <el-scrollbar wrap-style="max-height: 60vh;">
          <div style="text-align: left">
            <el-collapse accordion>
              <el-collapse-item
                name="1"
                v-for="(step, i) in actionFormbuilderSteps"
                :key="i + '_step'"
                class="rule-collapse"
              >
                <template slot="title">
                  <div style="width: 100%" class="ml-1">
                    <label v-if="step.type == 'TEMPLATE' && allTemplateFieldMap[step.template]">
                      <b>
                        {{ i + 1 }}.
                        {{ allTemplateFieldMap[step.template].name }}
                      </b>
                    </label>
                    <label v-if="step.type == 'ENTITY' && step.name">
                      <b>
                        {{ i + 1 }}.
                        {{ step.name }}
                      </b>
                    </label>
                  </div>
                </template>
                <el-table
                  :data="
                    step.type == 'TEMPLATE' && allTemplateFieldMap[step.template]
                      ? allTemplateFieldMap[step.template].fields.filter(
                          (e) => ['FORMULA', 'SIGNATURE'].indexOf(e.input_type) === -1
                        )
                      : step.type == 'ENTITY'
                        ? getAllEntityFieldsByForm(step.entity)
                        : []
                  "
                  size="mini"
                  border
                >
                  <el-table-column label="Field" prop="label" width="300">
                    <template slot-scope="{ row }">
                      <div style="font-weight: 500; font-size: 12px; color: #030503">
                        {{ row.label }}
                      </div>
                    </template>
                  </el-table-column>

                  <el-table-column label="Mapped with" prop="key" width="300">
                    <template slot-scope="scope">
                      <div class="d-flex">
                        <el-select
                          v-model="actionFormbuilderPrimaryFieldsMap[scope.row.key]"
                          placeholder="Update with"
                          clearable
                          filterable
                        >
                          <el-option
                            v-for="(field, i) in fetchTemplateFieldsByType(
                              [scope.row.inputType],
                              allFormbuilderFields,
                              scope.row.entity_id,
                              scope.row.key
                            )"
                            :key="field + i"
                            :value="field.template_key"
                            :label="field.label"
                          ></el-option>
                        </el-select>
                      </div>
                    </template>
                  </el-table-column>
                </el-table>
              </el-collapse-item>
            </el-collapse>
          </div>
        </el-scrollbar>
      </div>
    </dialog-component>
    <dialog-component
      :title="'Parent Formbuilder Fields Mapping'"
      :visible="openParentFormMappingDialog"
      @before-close="closeParentFormMappingDialog"
      :isShowFooter="false"
      :containerWidth="'52%'"
      :containerHeight="'70%'"
    >
      <div class="p-3">
        <el-scrollbar wrap-style="max-height: 60vh;">
          <div style="text-align: left">
            <el-collapse accordion>
              <el-collapse-item
                name="1"
                v-for="(step, i) in actionFormbuilderStepsForParentMapping"
                :key="i + '_step'"
                class="rule-collapse"
              >
                <template slot="title">
                  <div style="width: 100%" class="ml-1">
                    <label v-if="step.type == 'TEMPLATE' && allTemplateFieldMap[step.template]">
                      <b>
                        {{ i + 1 }}.
                        {{ allTemplateFieldMap[step.template].name }}
                      </b>
                    </label>
                    <label v-if="step.type == 'ENTITY' && step.name">
                      <b>
                        {{ i + 1 }}.
                        {{ step.name }}
                      </b>
                    </label>
                  </div>
                </template>
                <el-table
                  :data="
                    step.type == 'TEMPLATE' && allTemplateFieldMap[step.template]
                      ? allTemplateFieldMap[step.template].fields.filter(
                          (e) => ['FORMULA', 'SIGNATURE'].indexOf(e.input_type) === -1
                        )
                      : step.type == 'ENTITY'
                        ? getAllEntityFieldsByForm(step.entity)
                        : []
                  "
                  size="mini"
                  border
                >
                  <el-table-column label="Field" prop="label" width="300">
                    <template slot-scope="{ row }">
                      <div style="font-weight: 500; font-size: 12px; color: #030503">
                        {{ row.label }}
                      </div>
                    </template>
                  </el-table-column>

                  <el-table-column label="Mapped with" prop="key" width="300">
                    <template slot-scope="scope">
                      <div class="d-flex">
                        <el-select
                          v-model="actionFormbuilderForParentFieldsMap[scope.row.key]"
                          placeholder="Update with"
                          clearable
                          filterable
                        >
                          <el-option
                            v-for="(field, i) in fetchTemplateFieldsByType(
                              [scope.row.inputType],
                              allFormbuilderFields,
                              scope.row.entity_id,
                              scope.row.key
                            )"
                            :key="field + i"
                            :value="field.template_key"
                            :label="field.label"
                          ></el-option>
                        </el-select>
                      </div>
                    </template>
                  </el-table-column>
                </el-table>
              </el-collapse-item>
            </el-collapse>
          </div>
        </el-scrollbar>
      </div>
    </dialog-component>
  </div>
</template>

<script>
  import { postAPICall } from '../../helpers/httpHelper';
  import { fetchEntityById } from '../../repo/entityRepo';
  import { getAllFieldsArrayFromEntity } from '../../utils/formatter';
  import AutomationHelper from '@/mixins/AutomationHelper';
  import EntitiesHelper from '../../mixins/EntitiesHelper';
  import TemplateBuilderHelper from '@/mixins/templateBuilderHelper';
  export default {
    name: 'AdvancedApprovalDataActions',
    mixins: [AutomationHelper, EntitiesHelper, TemplateBuilderHelper],
    props: ['formBuilder', 'allFormbuilderFields', 'allFormbuilders'],
    data() {
      return {
        openParentFormMappingDialog: false,
        currentRuleIndex: -1,
        currentConditionIndex: -1,
        currentActionIndex: -1,
        actionFormbuilderPrimaryFieldsMap: {},
        actionFormbuilderSteps: [],
        actionFormbuilderStepsForParentMapping: [],
        actionFormbuilderForParentFieldsMap: {},
        allTemplateFieldMap: {},
        openFieldsMappingDialog: false,
        getEntityRecordsForTable: null,
        entityDataInfo: [],
        operatorsMap: {
          NUMBER: [
            {
              title: 'Equals (=)',
              value: '=',
            },
            {
              title: 'Greater Than (>)',
              value: '>',
            },
            {
              title: 'Greater Than or Equal(>=)',
              value: '>=',
            },
            {
              title: 'Less Than (<)',
              value: '<',
            },
            {
              title: 'Less Than or Equal(<=)',
              value: '<=',
            },
            {
              title: 'Not Exist',
              value: 'notexists',
            },
            {
              title: 'Exist',
              value: 'exists',
            },
            {
              title: 'Range',
              value: 'between',
            },
          ],
          PHONE: [
            {
              title: 'Exist',
              value: 'exists',
            },
            {
              title: 'Not Exist',
              value: 'notexists',
            },
          ],
          CONCATENATE: [
            {
              title: 'Exist',
              value: 'exists',
            },
            {
              title: 'Not Exist',
              value: 'notexists',
            },
          ],
          STRING: [
            {
              title: 'Equals (Case-sensitive)',
              value: '=case-sensitive',
            },
            {
              title: 'Equals (Case-insensitive)',
              value: '=case-insensitive',
            },
            {
              title: 'Text Includes',
              value: '=text-includes',
            },
            {
              title: 'Not Exist',
              value: 'notexists',
            },
            {
              title: 'Exist',
              value: 'exists',
            },
            {
              title: 'Starts With',
              value: '=starts-with',
            },
            {
              title: ' Not Starts With',
              value: '=not-starts-with',
            },
            {
              title: 'Ends With',
              value: '=ends-with',
            },
            {
              title: 'Not Ends With',
              value: '!ends-with',
            },
          ],
          PAYMENT: [
            {
              title: 'In',
              value: 'in',
            },
            {
              title: 'Not In',
              value: 'nin',
            },
            {
              title: 'Equals (Case-sensitive)',
              value: '=case-sensitive',
            },
            {
              title: ' Not Equals (Case-sensitive)',
              value: '!=case-sensitive',
            },
            {
              title: 'Equals (Case-insensitive)',
              value: '=case-insensitive',
            },
            {
              title: ' Not Equals (Case-insensitive)',
              value: '!=case-insensitive',
            },
            {
              title: 'Exist',
              value: 'exists',
            },
            {
              title: 'Not Exist',
              value: 'notexists',
            },
          ],
          OBJECT_ID: [
            {
              title: 'In',
              value: 'in',
            },
            {
              title: 'Not In',
              value: 'nin',
            },
            {
              title: 'Not Exist',
              value: 'notexists',
            },
            {
              title: 'Exist',
              value: 'exists',
            },
          ],
          DATE: [
            {
              title: 'Real time picker',
              value: 'real_time',
            },
            {
              title: 'Not In',
              value: 'nin',
            },
            {
              title: 'Match with weekdays',
              value: 'weekdays',
            },
            {
              title: 'Equals (=)',
              value: '=',
            },
            {
              title: 'Greater Than (>)',
              value: '>',
            },
            {
              title: 'Greater Than or Equal(>=)',
              value: '>=',
            },
            {
              title: 'Less Than (<)',
              value: '<',
            },
            {
              title: 'Less Than or Equal(<=)',
              value: '<=',
            },
            {
              title: 'Not Exist',
              value: 'notexists',
            },
            {
              title: 'Exist',
              value: 'exists',
            },
            {
              title: 'Between',
              value: 'between',
            },
            {
              title: 'NotBetween',
              value: 'notbetween',
            },
            {
              title: 'Before',
              value: 'before',
            },
            {
              title: 'After',
              value: 'after',
            },
          ],
          TIME: [
            {
              title: 'Equals (=)',
              value: '=',
            },
            {
              title: 'Greater Than (>)',
              value: '>',
            },
            {
              title: 'Greater Than or Equal(>=)',
              value: '>=',
            },
            {
              title: 'Less Than (<)',
              value: '<',
            },
            {
              title: 'Less Than or Equal(<=)',
              value: '<=',
            },
            {
              title: 'Not Exist',
              value: 'notexists',
            },
            {
              title: 'Exist',
              value: 'exists',
            },
            {
              title: 'Between',
              value: 'between',
            },
            {
              title: 'NotBetween',
              value: 'notbetween',
            },
          ],
          RELATION: [
            {
              title: 'In',
              value: 'in',
            },
            {
              title: 'Not In',
              value: 'nin',
            },
            {
              title: 'Not Exist',
              value: 'notexists',
            },
            {
              title: 'Exist',
              value: 'exists',
            },
            {
              title: 'Filter',
              value: 'filter',
            },
          ],
          FILE: [
            {
              title: 'Not Exist',
              value: 'notexists',
            },
            {
              title: 'Exist',
              value: 'exists',
            },
          ],
          ARRAY: [
            {
              title: 'In',
              value: 'in',
            },
            {
              title: 'Not In',
              value: 'nin',
            },
            {
              title: 'Exist',
              value: 'exists',
            },
            {
              title: 'Not Exist',
              value: 'notexists',
            },
          ],
          DATE_TIME: [
            {
              title: 'Exist',
              value: 'exists',
            },
            {
              title: 'Not Exist',
              value: 'notexists',
            },
            {
              title: 'Date Includes',
              value: 'dateinclude',
            },
            {
              title: 'Date Not Includes',
              value: 'notdateinclude',
            },
          ],
          WEEKDAYS: [
            {
              title: 'Exist',
              value: 'exists',
            },
            {
              title: 'Not Exist',
              value: 'notexists',
            },
            {
              title: 'In',
              value: 'in',
            },
            {
              title: 'Not In',
              value: 'nin',
            },
          ],
          TIME_RANGE: [
            {
              title: 'Exist',
              value: 'exists',
            },
            {
              title: 'Not Exist',
              value: 'notexists',
            },
            {
              title: 'Time includes',
              value: 'timeinclude',
            },
          ],
          YES_OR_NO: [
            {
              title: 'Equals',
              value: '=case-sensitive',
            },
            {
              title: 'Exist',
              value: 'exists',
            },
            {
              title: 'Not Exist',
              value: 'notexists',
            },
          ],
          CURRENCY: [
            {
              title: 'Equals (=)',
              value: '=',
            },
            {
              title: 'Greater Than (>)',
              value: '>',
            },
            {
              title: 'Greater Than or Equal(>=)',
              value: '>=',
            },
            {
              title: 'Less Than (<)',
              value: '<',
            },
            {
              title: 'Less Than or Equal(<=)',
              value: '<=',
            },
            {
              title: 'Not Exist',
              value: 'notexists',
            },
            {
              title: 'Exist',
              value: 'exists',
            },
            {
              title: 'Range',
              value: 'between',
            },
          ],
          BOOLEAN: [
            {
              title: 'In',
              value: 'in',
            },
            {
              title: 'Not In',
              value: 'nin',
            },
            {
              title: 'Exist',
              value: 'exists',
            },
            {
              title: 'Not Exist',
              value: 'notexists',
            },
          ],
          DATE_RANGE: [
            {
              title: 'Exist',
              value: 'exists',
            },
            {
              title: 'Not Exist',
              value: 'notexists',
            },
            {
              title: 'Date includes',
              value: 'dateinclude',
            },
          ],
        },
        operatorValueTypeMap: [
          {
            value_type: 'VALUE',
            operators: [
              '=',
              '<=',
              '<',
              '>',
              '>=',
              '=case-insensitive',
              '=case-sensitive',
              '=text-includes',
              '!=',
              'dateinclude',
              'timeinclude',
              'in',
              'nin',
              '=starts-with',
              '=not-starts-with',
              '=ends-with',
              '!ends-with',
              'after',
              'before',
            ],
            data_types: [
              'NUMBER',
              'STRING',
              'DATE',
              'TIME',
              'DATE_TIME',
              'TIME_RANGE',
              'YES_OR_NO',
              'CURRENCY',
              'LIST',
              'WEEKDAYS',
              'BOOLEAN',
              'created_at',
              'updated_at',
              'created_by',
              'updated_by',
            ],
          },
          {
            value_type: 'GLOBAL_VARIABLE',
            operators: [
              '=',
              '<=',
              '>=',
              '<',
              '>',
              '=case-insensitive',
              '=text-includes',
              '=case-sensitive',
              '!=',
            ],
            data_types: ['NUMBER', 'STRING'],
          },
          {
            value_type: 'NEW_LIST',
            operators: ['in', 'nin'],
            data_types: ['NUMBER'],
          },

          {
            value_type: 'ENTITY_DATA_LIST',
            operators: ['in', 'nin'],
            data_types: ['OBJECT_ID', 'RELATION'],
          },
          {
            value_type: 'REAL_TIME_PICKER',
            operators: ['dateinclude'],
            data_types: ['DATE_TIME'],
          },
          {
            value_type: 'CURRENT_DATE',
            operators: ['=', '<=', '<', '>', '>='],
            data_types: ['DATE_TIME', 'DATE'],
          },
        ],
        weekdays_options: [
          { name: 'Monday', value: 1, key: 'MON' },
          { name: 'Tuesday', value: 2, key: 'TUE' },
          { name: 'Wednesday', value: 3, key: 'WED' },
          { name: 'Thursday', value: 4, key: 'THU' },
          { name: 'Friday', value: 5, key: 'FRI' },
          { name: 'Saturday', value: 6, key: 'SAT' },
          { name: 'Sunday', value: 0, key: 'SUN' },
        ],
        starRating_options: [
          { name: '1', value: 1 },
          { name: '1.5', value: 1.5 },
          { name: '2', value: 2 },
          { name: '2.5', value: 2.5 },
          { name: '3', value: 3 },
          { name: '3.5', value: 3.5 },
          { name: '4', value: 4 },
          { name: '4.5', value: 4.5 },
          { name: '5', value: 5 },
        ],
        allEntities: [],
        allTemplates: [],
      };
    },
    async mounted() {
      [this.allEntities, this.allTemplates] = await Promise.all([
        this.fetchAllEntities(),
        this.fetchAllTemplates(),
      ]);
      (this.allTemplates || []).forEach((template) => {
        this.allTemplateFieldMap[template._id] = {
          name: template.name,
          id: template._id,
          fields: this.getFieldsFromTemplate(template, true, true),
        };
      });
      this.allEntities = this.allEntities.map((entity) => {
        if (entity?.templates) {
          entity.templates = entity.templates.map((template) => {
            template.templateInfo = this.allTemplates.find(
              (temp) => temp._id == template.template_id
            );
            return template;
          });
        }
        return entity;
      });
      if (!this.formBuilder?.conditional_approval_data_actions?.length) {
        this.formBuilder.conditional_approval_data_actions.push({
          conditions: [
            {
              type: '',
              field: '',
              operator: '',
              value_type: '',
              value_from: '',
              value: null,
              query_type: 'AND',
            },
          ],
          actions: [],
        });
      }
    },
    computed: {
      getLevels() {
        return this.formBuilder?.approval_configuration_type == 'ADVANCED'
          ? this.formBuilder.advanced_approval_users
          : this.formBuilder.approval_users;
      },
    },
    methods: {
      openParentFormFieldsMapping(ruleIndex, aIndex, action) {
        this.currentRuleIndex = ruleIndex;
        this.currentActionIndex = aIndex;
        this.actionFormbuilderStepsForParentMapping = this.getFormbuilderAllFields(
          action.parent_formbuilder_details_id
        );
        if (action?.parent_form_mapped_data) {
          this.actionFormbuilderForParentFieldsMap = action.parent_form_mapped_data;
        }
        this.openParentFormMappingDialog = true;
      },
      closeParentFormMappingDialog() {
        this.openParentFormMappingDialog = false;
        this.formBuilder.conditional_approval_data_actions[this.currentRuleIndex].actions[
          this.currentActionIndex
        ]['parent_form_mapped_data'] = JSON.parse(
          JSON.stringify(this.actionFormbuilderForParentFieldsMap)
        );
        this.currentRuleIndex = -1;
        this.currentActionIndex = -1;
      },
      removeRule(ruleIndex) {
        this.formBuilder.conditional_approval_data_actions.splice(ruleIndex, 1);
      },
      getAllEntityFieldsByForm(entity_id) {
        let entity = this.allEntities.find((e) => e._id == entity_id);
        let fields = [];
        if (entity) {
          entity.templates.forEach((tem) => {
            let templateFields = this.getFieldsFromTemplate(tem.templateInfo, false, false);
            // Add entity_id# prefix to each field key
            templateFields = templateFields.map((f) => ({
              ...f,
              key: `${entity_id}#${f.key}`,
            }));
            fields = [...fields, ...templateFields];
          });
        }
        return fields;
      },
      addRule() {
        this.formBuilder.conditional_approval_data_actions.push({
          conditions: [],
          actions: [],
        });
      },

      addCondition(rule) {
        rule.conditions.push({
          field: '',
          operator: '',
          value_type: '',
          value: '',
          query_type: 'AND',
        });
      },
      removeCondition(rule, index) {
        rule.conditions.splice(index, 1);
      },
      addAction(rule) {
        rule.actions.push({
          workflow_level: '',
          action_type: '',
          trigger_formbuilder_id: '',
          resume_formbuilder_id: '',
          isRepeat: true,
          mapped_data: {},
          parent_formbuilder_details_id: '',
          parent_form_mapped_data: {},
        });
      },
      removeAction(rule, index) {
        rule.actions.splice(index, 1);
      },
      openFormFieldsMapping(ruleIndex, aIndex, action) {
        this.currentRuleIndex = ruleIndex;
        this.currentActionIndex = aIndex;
        this.actionFormbuilderSteps = this.getFormbuilderAllFields(action.trigger_formbuilder_id);
        if (action?.mapped_data) {
          this.actionFormbuilderPrimaryFieldsMap = action.mapped_data;
        }
        this.openFieldsMappingDialog = true;
      },
      closeFieldsMappingDialog() {
        this.openFieldsMappingDialog = false;
        this.formBuilder.conditional_approval_data_actions[this.currentRuleIndex].actions[
          this.currentActionIndex
        ]['mapped_data'] = JSON.parse(JSON.stringify(this.actionFormbuilderPrimaryFieldsMap));
        this.currentRuleIndex = -1;
        this.currentActionIndex = -1;
      },
      fieldType(filter, type) {
        if (filter.field) {
          let field = this.allFormbuilderFields.find(
            (field) => field.key == filter.field || field.template_key == filter.field
          );
          return field?.input_type == type;
        }
        return [];
      },
      getCurrentFieldOptions(filter) {
        if (filter.selectField === 'created_at') {
          filter.data_type = 'DATE';
          filter.input_type = 'created_at';
          return;
        }
        if (filter.selectField === 'updated_at') {
          filter.data_type = 'DATE';
          filter.input_type = 'updated_at';
          return;
        }
        if (filter.selectField === 'created_by') {
          filter.data_type = 'created_by';
          filter.input_type = 'created_by';
          return;
        }
        if (filter.selectField === 'updated_by') {
          filter.data_type = 'updated_by';
          filter.input_type = 'updated_by';
          return;
        }
        if (filter.field) {
          let field = this.allFormbuilderFields.find(
            (field) => field.key == filter.field || field.template_key == filter.field
          );
          if (field?.input_type == 'LIST' && field?.list_data) {
            return field.list_data;
          }
          if (field && field.input_type == 'PAY_BUTTON' && field.pay_status) {
            return field.pay_status;
          } else if (field?.input_type == 'WEEKDAYS') {
            let weekdays_names = this.weekdays_options.map((weekday) => weekday.name);
            return weekdays_names;
          } else if (
            field?.input_type == 'STAR_RATING' ||
            (field?.input_type == 'ENTITY_VARIABLE' && field?.inputType == 'STAR_RATING')
          ) {
            let starRating_values = this.starRating_options.map((starRating) => starRating.value);
            return starRating_values;
          } else {
            return field?.options || [];
          }
        } else {
          return [];
        }
      },
      getCurrentFieldOperators(filter) {
        if (
          filter.field == 'status' ||
          filter.data_type == 'STATUS' ||
          filter.field == 'isCreatedFromFormbuilder'
        ) {
          return [
            {
              title: 'In',
              value: 'in',
            },
            {
              title: 'Not In',
              value: 'nin',
            },
          ];
        }
        if (filter.data_type == 'created_at' || filter.data_type == 'updated_at') {
          return [
            {
              title: 'Greater Than (>)',
              value: '>',
            },
            {
              title: 'Less Than (<)',
              value: '<',
            },
            {
              title: 'Last Month',
              value: 'lastmonth',
            },
            {
              title: 'This Month',
              value: 'thismonth',
            },
          ];
        }
        if (filter.data_type == 'created_by' || filter.data_type == 'updated_by') {
          return [
            {
              title: 'In',
              value: 'in',
            },
            {
              title: 'Not In',
              value: 'nin',
            },
          ];
        }
        if (filter.field) {
          if (filter.data_type) {
            return this.operatorsMap[filter.data_type];
          } else {
            let field = this.allFormbuilderFields.find(
              (field) => field.key == filter.field || field.template_key == filter.field
            );
            if (field && field.data_type && field.result_type && field.result_type.length) {
              return this.operatorsMap[field.result_type];
            } else if (field && field.data_type) {
              return this.operatorsMap[field.data_type];
            } else {
              return [];
            }
          }
        } else {
          return [];
        }
      },
      getTypeFromInputType(inputType) {
        const obj = {
          SINGLE_LINE_TEXT: 'STRING',
          MULTI_LINE_TEXT: 'STRING',
          RANDOM_TEXT: 'STRING',
          DATE: 'DATE',
          DATE_TIME: 'DATE',
          TIME: 'TIME',
          NUMBER: 'NUMBER',
          FILE: 'FILE',
          SELECT: 'ARRAY',
          YES_OR_NO: 'YES_OR_NO',
          MULTI_SELECT: 'ARRAY',
          CHECKBOX_GROUP: 'ARRAY',
          RADIO_BUTTON_GROUP: 'ARRAY',
          CHECKBOX: 'BOOLEAN',
          RADIO: 'STRING',
          HEADING: 'STRING',
          SIGNATURE: 'FILE',
          GLOBAL_VARIABLE: 'OBJECT_ID',
          TEMPLATE_FIELD: 'OBJECT_ID',
          ENTITY: 'OBJECT_ID',
          IMAGE: 'STRING',
          PASSWORD: 'STRING',
          MASKED: 'STRING',
          EMAIL: 'EMAIL',
          PHONE: 'PHONE',
          FAX: 'NUMBER',
          ZIP_CODE: 'ZIP_CODE',
          CONCATENATE: 'CONCATENATE',
          CURRENCY: 'NUMBER',
          PHONE_COUNTRY_CODE: 'PHONE',
          QUESTION: 'STRING',
          DATE_TIME_RANGE: 'DATE_TIME',
          DATE_RANGE: 'DATE_RANGE',
          WEEKDAYS: 'WEEKDAYS',
          TIME_RANGE: 'TIME_RANGE',
          RELATION: 'RELATION',
          LIST: 'ARRAY',
          Hyper_link: 'STRING',
          STAR_RATING: 'ARRAY',
          PAY_BUTTON: 'PAYMENT',
          AGGREGATE_FUNCTION: 'NUMBER',
          AUTO_INCREMENT_NUMBER: 'STRING',
        };
        return obj[inputType];
      },
      async checkCurrentFieldData(fieldKey, filter) {
        if (fieldKey == 'status') {
          filter.data_type = 'STATUS';
          filter.data_source = 'VALUE';
          return;
        }
        if (fieldKey == 'isCreatedFromFormbuilder') {
          filter.data_type = 'isCreatedFromFormbuilder';
          filter.data_source = 'VALUE';
          return;
        }
        if (fieldKey == 'created_at') {
          filter.data_type = 'DATE';
          filter.input_type = 'created_at';

          return;
        }
        if (fieldKey == 'updated_at') {
          filter.data_type = 'DATE';
          filter.input_type = 'updated_at';

          return;
        }
        if (fieldKey == 'created_by') {
          filter.data_type = 'created_by';
          filter.input_type = 'created_by';

          return;
        }
        if (fieldKey == 'updated_by') {
          filter.data_type = 'updated_by';
          filter.input_type = 'updated_by';

          return;
        }
        let field = this.allFormbuilderFields.find(
          (field) => field.key == fieldKey || field.template_key == fieldKey
        );
        if (field.inputType == 'DATE_TIME' || field.inputType == 'DATE') {
          filter.is_date_time = true;
        } else {
          filter.is_date_time = false;
        }
        if (field?.data_table_key) {
          filter.data_table_key = field.data_table_key;
        }
        if (field.input_type == 'WEEKDAYS') {
          filter.allow_multiple = field.allow_multiple;
        } else if (field.input_type == 'FORMULA') {
          filter.data_type = field.result_type;
          filter.is_formula = true;
        } else if (field.input_type == 'ENTITY_VARIABLE' && field.inputType == 'FORMULA') {
          filter.data_type = this.getTypeFromInputType(field.result_type);
          field.data_type = this.getTypeFromInputType(field.result_type);
        } else {
          filter.data_type = this.getTypeFromInputType(field.inputType);
          field.data_type = this.getTypeFromInputType(field.inputType);
        }
        if (field.input_type == 'RELATION' || field.data_type == 'OBJECT_ID') {
          filter.relation_entity_id = field.entity_id;
        }
        this.$set(filter, 'data_source', '');
        if (field.inputType == 'SELECT' || field.inputType == 'MULTI_SELECT') {
          this.$set(filter, 'value', []);
        }
        // ENtity type select fields are becoming emtpty if value have array of entity data ids.
        // else {
        //   this.$set(filter, "value", "");
        // }
        filter.input_type = field.input_type;
        await this.getEntilistAsOptions(filter);
      },
      async getEntilistAsOptions(filter) {
        let fieldKey = filter.field;
        let field = this.allFormbuilderFields.find(
          (field) => field.key == fieldKey || field.template_key == fieldKey
        );
        if (field.inputType == 'ENTITY' || field.input_type == 'RELATION') {
          await this.fetchEntityPrimaryFieldsData(field.entity_id);
        }
      },
      async fetchEntityPrimaryFieldsData(entity_id) {
        let currentEntity = await fetchEntityById(entity_id);
        let params = {
          entity_id: entity_id,
          template_fields_data: getAllFieldsArrayFromEntity(currentEntity),
          format_data: true,
          limit: 100,
          filters: [],
        };
        const v2Response = await postAPICall('POST', '/entities-data/entity/data/v2', params);
        this.entityRecordsForTableLocal = v2Response?.data;
        if (this.entityRecordsForTableLocal && this.entityRecordsForTableLocal.data) {
          this.entityDataInfo.push({
            entity: currentEntity,
            data: this.entityRecordsForTableLocal.data,
            entity_id: entity_id,
          });
        }
      },
      entilistAsOptions(filter) {
        let field = this.allFormbuilderFields.find(
          (e) => e.key == filter.field || e.template_key == filter.field
        );
        if (field && field.entity_id && this.entityDataInfo && this.entityDataInfo.length) {
          let selectedEntity = this.entityDataInfo.find((e) => e.entity_id === field.entity_id);
          if (selectedEntity && selectedEntity.data) {
            return selectedEntity.data;
          }
        }

        return [];
      },
      getLabel(item, i) {
        let selectedEntity = (this.entityDataInfo || []).find((e) => e.entity_id == item.entity_id);
        if (selectedEntity && selectedEntity.entity) {
          selectedEntity = selectedEntity.entity;
        }
        let name =
          item &&
          item.entity_prime_data &&
          Object.keys(item.entity_prime_data) &&
          item.entity_prime_data[Object.keys(item.entity_prime_data)[0]]
            ? item.entity_prime_data[Object.keys(item.entity_prime_data)[0]]
            : 'data ' + i;
        if (selectedEntity && selectedEntity.primaryFields) {
          let primaryField = selectedEntity.primaryFields[0];
          let key = primaryField && primaryField['key'] ? primaryField['key'] : null;
          if (key) {
            name =
              item.entityData &&
              primaryField.template_id &&
              item.entityData[primaryField.template_id]
                ? item.entityData[primaryField.template_id][key + '/name']
                  ? item.entityData[primaryField.template_id][key + '/name']
                  : item.entityData[primaryField.template_id][key]
                    ? item.entityData[primaryField.template_id][key]
                    : ''
                : '';
            if (!name && item.entity_prime_data) {
              name = item.entity_prime_data[key + '/name']
                ? item.entity_prime_data[key + '/name']
                : item.entity_prime_data[key];
            }
          }
        }
        return name;
      },
    },
  };
</script>

<style scoped>
  .rule-box {
    border: 1px solid #ddd;
    padding: 16px;
    margin-bottom: 20px;
    border-radius: 8px;
    width: 85%;
  }

  .section {
    margin-bottom: 15px;
  }

  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
    border-bottom: 1px solid #ccc;
    /* light divider */
    padding: 5px;
    /* adds spacing above divider */
    background-color: #e3f2fd;
  }

  .row {
    gap: 5px;
    align-items: center;
    margin-bottom: 8px;
  }

  h2 {
    color: rgb(47, 49, 68);
  }

  .actions-section {
    margin-bottom: 20px;
  }

  .actions-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 0.5px solid #ccc;
    margin-bottom: 10px;
    padding: 5px;
    background-color: #e3f2fd;
  }

  .actions-title {
    color: black;
    margin: 0;
  }

  .actions-row {
    display: flex;
    align-items: center;
    margin-bottom: 10px;
    gap: 15px;
    /* space between fields */
  }

  .actions-field {
    display: flex;
    flex-direction: column;
  }

  .actions-label {
    font-size: 12px;
    font-weight: 500;
    margin-bottom: 2px;
  }

  .actions-input {
    width: 180px;
    /* adjust field width */
  }

  .remove-action-btn {
    margin-left: auto;
  }

  .rule-title {
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 15px;
    color: #2f3144;
  }

  .section-title {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: #333;
  }

  .rule-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
</style>
